<template>
  <RevDrawer
    :name="MODAL_NAME"
    size="large"
    :title="
      i18n(
        translations.addNewUserTitle,
      ) /* TODO missing translation for 'edit' */
    "
    @open="handleDrawerOpen"
  >
    <template #body>
      <RevInfoBlock
        v-if="apiError"
        class="my-16"
        :icon="IconWarning"
        :title="i18n(translations.errorTitle)"
        variant="danger"
      >
        {{ apiError }}
      </RevInfoBlock>
      <div class="body-1 mb-8 mt-16">
        {{
          i18n(
            translations.addNewUserDetails,
          ) /* TODO missing translation for 'edit' */
        }}
      </div>
      <RevForm :initial-values="userData" :validate @submit="handleSubmit">
        <template #default="{ values, errors, hasError }">
          <div :class="{ 'grid grid-cols-2 gap-8': isKanaNameEnabled }">
            <RevInputText
              id="firstName"
              v-model="values.firstName"
              class="mb-8"
              :error="errors.firstName"
              :label="
                i18n(
                  isKanaNameEnabled
                    ? translations.firstNameKanji
                    : translations.firstName,
                )
              "
            />
            <RevInputText
              v-if="isKanaNameEnabled"
              id="katakana-firstName"
              v-model="values.firstNamePronunciation"
              class="mb-8"
              :error="errors.firstNamePronunciation"
              :label="i18n(translations.firstNamePronunciation)"
            />
          </div>
          <div :class="{ 'grid grid-cols-2 gap-8': isKanaNameEnabled }">
            <RevInputText
              id="lastName"
              v-model="values.lastName"
              class="mb-8"
              :error="errors.lastName"
              :label="
                i18n(
                  isKanaNameEnabled
                    ? translations.lastNameKanji
                    : translations.lastName,
                )
              "
            />
            <RevInputText
              v-if="isKanaNameEnabled"
              id="katakana-lastName"
              v-model="values.lastNamePronunciation"
              class="mb-8"
              :error="errors.lastNamePronunciation"
              :label="i18n(translations.lastNamePronunciation)"
            />
          </div>
          <RevInputText
            id="email"
            v-model="values.email"
            autocomplete="email"
            class="mb-8"
            data-cs-mask
            :error="errors.email"
            :label="i18n(translations.email)"
            type="email"
          />
          <div class="body-2 mb-24">
            {{ i18n(translations.emailInfoLabel) }}
          </div>
          <RevCard v-if="canSetPermission" class="mb-16 p-16">
            <div class="heading-3">
              {{ i18n(translations.managePermissions) }}
            </div>
            <div class="body-1 mb-24">
              {{ i18n(translations.managePermissionsInfo) }}
            </div>
            <div class="body-1-bold grid grid-cols-3">
              <div>{{ i18n(translations.page) }}</div>
              <div>{{ i18n(translations.noAccess) }}</div>
              <div>{{ i18n(translations.editingAccess) }}</div>
            </div>
            <RevDivider class="my-16" />
            <div
              v-for="scope in scopes"
              :key="scope.name"
              class="mb-16 grid grid-cols-3"
            >
              <div>{{ i18n(scope.translationKey) }}</div>
              <RevRadio
                :id="scope.name + '-no-access'"
                v-model="values.permissions[scope.name]"
                :aria-label="
                  i18n(translations.noAccessPermission, {
                    pageName: scope.name,
                  })
                "
                :value="PERMISSION_TYPES.NO_ACCESS"
              >
                <template #label>&nbsp;</template>
              </RevRadio>
              <RevRadio
                :id="scope.name + '-editing-access'"
                v-model="values.permissions[scope.name]"
                :aria-label="
                  i18n(translations.editPermission, { pageName: scope.name })
                "
                :value="PERMISSION_TYPES.EDIT"
              >
                <template #label>&nbsp;</template>
              </RevRadio>
            </div>
          </RevCard>
          <RevButton
            class="mt-8 w-full"
            :disabled="hasError || isSubmitting"
            :loading="isSubmitting"
            type="submit"
            variant="primary"
          >
            {{ submitLabel }}
          </RevButton>
        </template>
      </RevForm>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { sellerUsersAPI } from '@backmarket/http-api'
import type { SellerUser } from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-users'
import { AddressFieldsValidators } from '@backmarket/nuxt-module-address/AddressFieldsValidators'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import { RevButton } from '@ds/components/Button'
import { RevCard } from '@ds/components/Card'
import { RevDivider } from '@ds/components/Divider'
import { RevDrawer } from '@ds/components/Drawer'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  required,
} from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputText } from '@ds/components/InputText'
import { closeModal } from '@ds/components/ModalBase'
import { RevRadio } from '@ds/components/Radio'
import { IconWarning } from '@ds/icons/IconWarning'

import {
  PERMISSION_TYPES,
  SCOPES,
  type ScopeName,
  type ScopePermissionType,
} from '~/constants/permissions'
import { REGEXP_VALID_EMAIL } from '~/constants/regex'
import { useBusinessFeaturesStore } from '~/scopes/config/stores/businessFeaturesStore'

import { MODAL_NAMES } from '../../../../Profile.constants'

import translations from './UserFormDrawer.translations'
import type { FormSellerUserValues } from './UserFormDrawer.types'

const MODAL_NAME = MODAL_NAMES.USER_PERMISSIONS.USER_FORM

const props = defineProps<{
  user?: SellerUser
}>()

const emit = defineEmits(['user-created', 'user-edited'])

const i18n = useI18n()
const { isFeatureEnabled } = useBusinessFeaturesStore()
const { openSuccessToast } = useTheToast()

const isKanaNameEnabled = isFeatureEnabled('kanaNameEnabled')
const isInsightsFeatureEnabled = isFeatureEnabled('insightsEnabled')

const addressValidators = AddressFieldsValidators(i18n)

const apiError = ref('')
const isInEditMode = computed(() => !!props.user)
const isSubmitting = ref(false)

const scopes = computed(() =>
  Object.values(SCOPES).filter(
    ({ name }) => name !== SCOPES.INSIGHTS.name || isInsightsFeatureEnabled,
  ),
)

const userData = computed<FormSellerUserValues>(() => {
  const values = {
    email: '',
    firstName: '',
    firstNamePronunciation: '',
    lastName: '',
    lastNamePronunciation: '',
    ...props.user,
    permissions: {} as FormSellerUserValues['permissions'],
  }
  if (!isInEditMode.value) {
    // If it's a new user, we initialize the permission with each default permission page
    scopes.value.forEach(({ name, defaultPermissionType }) => {
      values.permissions[name] = defaultPermissionType
    })
  } else {
    const permissions = {} as FormSellerUserValues['permissions']
    // If it's an edit, since the backend does not return anything in case of no access permission,
    // we first initialize every permission to no access
    scopes.value.forEach(({ name }) => {
      permissions[name] = PERMISSION_TYPES.NO_ACCESS
    })
    // Then change the pages who actually have a permission. The format receive by the backend is:
    // PAGE.PERMISSION (ex: home.edit), that's why we need to split the permission to retreive
    // the info we are looking for
    props.user?.permissions.forEach((permission) => {
      const [scopeName, permissionType] = permission.split('.') as [
        ScopeName,
        ScopePermissionType,
      ]
      permissions[scopeName] = permissionType
    })
    values.permissions = permissions
  }

  return values
})

const canSetPermission = computed(() => !props.user?.isAdmin)
const submitLabel = computed(() =>
  i18n(
    isInEditMode.value
      ? translations.editUserAction
      : translations.addNewUserAction,
  ),
)

const validate = makeValidate<FormSellerUserValues>({
  email: [
    required(i18n(translations.required)),
    matchingRegExp(REGEXP_VALID_EMAIL, i18n(translations.invalidEmail)),
  ],
  firstName: [
    addressValidators.firstName.required,
    addressValidators.firstName.maxLength,
    // TODO fix ts issue
    // addressValidators.firstName.valid,
  ],
  lastName: [
    addressValidators.lastName.required,
    addressValidators.lastName.maxLength,
    // TODO fix ts issue
    // addressValidators.lastName.valid,
  ],
  firstNamePronunciation: [],
  lastNamePronunciation: [],
  permissions: [],
})

function handleDrawerOpen() {
  apiError.value = ''
  isSubmitting.value = false
}

async function handleSubmit(user: FormSellerUserValues) {
  apiError.value = ''
  isSubmitting.value = true
  try {
    const body: Partial<SellerUser> = {
      firstName: user.firstName,
      firstNamePronunciation: isKanaNameEnabled
        ? user.firstNamePronunciation
        : undefined,
      lastName: user.lastName,
      lastNamePronunciation: isKanaNameEnabled
        ? user.lastNamePronunciation
        : undefined,
      email: user.email,
      permissions: [],
    }
    if (canSetPermission.value) {
      body.permissions = (
        Object.entries(user.permissions || {}) as [
          ScopeName,
          ScopePermissionType,
        ][]
      ).reduce(
        (permissions, [scopeName, permissionType]) => {
          // For no access permission, backend expect nothing, so we need to remove
          // pages that should not be accessed
          if (permissionType !== PERMISSION_TYPES.NO_ACCESS) {
            // Then, we transform the permission in the expected backend format,
            // meaning: PAGE.PERMISSION (ex: home.edit)
            permissions.push(`${scopeName}.${permissionType}`)
          }

          return permissions
        },
        [] as SellerUser['permissions'],
      )
    }
    if (isInEditMode.value) {
      await $httpFetch(sellerUsersAPI.patchSellerUser, {
        pathParams: { userId: props.user?.id },
        body,
      })
      emit('user-edited')
      openSuccessToast({
        title: i18n(translations.userEditedTitle),
        content: i18n(translations.userEditedContent),
      })
    } else {
      await $httpFetch(sellerUsersAPI.postSellerUser, { body })
      emit('user-created')
      openSuccessToast({
        title: i18n(translations.newUserAdded),
        content: i18n(translations.nextStep),
      })
    }
    closeModal(MODAL_NAME)
  } catch (error) {
    apiError.value = getApiErrorMessage(error as unknown as Error)
  } finally {
    isSubmitting.value = false
  }
}
</script>
